/**
 * Single source of truth for app module metadata and lazy page chunk loaders.
 *
 * ## When to edit this file
 *
 * Add or change a private app module here — one object in `moduleDefinitions`.
 * Do **not** duplicate entries in `routeChunkLoaders.js`, `appModuleKeys.js`, or
 * hand-edit `ROUTE_NAMESPACES` in `i18n.js`; those are derived automatically.
 *
 * ## Derived consumers (read-only — do not edit for module changes)
 *
 * | Export / file | What it gets |
 * | --- | --- |
 * | `toRegistryEntry()` → `registryEntries` | Metadata without `loadPage` / `loadMobilePage` |
 * | `routeChunkLoaders.js` | `ROUTE_CHUNK_LOADERS_BY_ROUTE_ID` from `routing.routeId` + loaders |
 * | `appModuleKeys.js` | `APP_MODULE_KEYS` enum from each `key` string |
 * | `appModuleI18n.js` | Route → translation namespace map from `i18n` blocks |
 * | `appModuleCatalog.js` | Frozen lookup map from `registryEntries` |
 * | `appModuleIcons.js` | Resolves `sidebar.iconName` → React icons at UI boundaries |
 *
 * ## Why chunk loaders live here
 *
 * The original split kept **react-icons / Ant Design** out of the login cold-start path.
 *
 * ## Required fields per module
 *
 * - `key` — PascalCase string matching API `userData.modules` (e.g. `"Obligations"`)
 * - `routePaths`, `defaultPath`, `access`, `sidebar`, `routing`, `i18n`
 * - `loadPage` — `() => import(...)` for `routing.routeId`
 * - `loadMobilePage` — only when `routing.mobileRouteId` is set
 *
 * Optional: `routing.postLoginChunkPreloadOrder`, `i18n.includeEmbeddedWidgets`,
 * `i18n.routeOverrides`, `devOnly`, `assignable`, `assignmentOption`.
 *
 * @module modules/appModuleDefinitions
 * @see docs/Modules/adding-a-module.md
 * @see docs/runtime-preload.md
 */

/**
 * @typedef {Object} ModuleAccessConfig
 * @property {"module"|"authenticated"} type
 * @property {string[]} [deniedRoles]
 * @property {string[]} [requiredRoles]
 */

/**
 * @typedef {Object} ModuleRoutingConfig
 * @property {"flat"|"shell"} mode
 * @property {string} routeId — stable id for chunk loader map and React.lazy
 * @property {string} [mountPath] — required when `mode === "shell"`
 * @property {boolean} [mobileEnabled]
 * @property {string} [mobileRouteId]
 * @property {number} [postLoginChunkPreloadOrder] — lower runs earlier in background queue
 * @property {"standard"|"evaluator"} [postLoginChunkPreloadAudience]
 */

/**
 * @typedef {Object} ModuleI18nConfig
 * @property {string[]} namespaces — route-specific JSON (not shell chrome)
 * @property {boolean} [includeEmbeddedWidgets] — append obligationDetails + ai at preload
 * @property {Array<{ path: string, namespaces: string[] }>} [routeOverrides]
 */

/**
 * @typedef {Object} ModuleDefinition
 * @property {string} key
 * @property {string} label
 * @property {string} labelKey — `common.json` sidebar key
 * @property {string[]} routePaths
 * @property {string} defaultPath
 * @property {ModuleAccessConfig} access
 * @property {Object} sidebar — `iconName` resolved in appModuleIcons.js
 * @property {ModuleRoutingConfig} routing
 * @property {ModuleI18nConfig} i18n
 * @property {boolean} [assignable]
 * @property {Object} [assignmentOption]
 * @property {boolean} [devOnly]
 * @property {() => Promise<unknown>} loadPage
 * @property {() => Promise<unknown>} [loadMobilePage]
 */

/** @type {ModuleDefinition[]} */
export const moduleDefinitions = [
  {
    key: "Dashboard",
    label: "Dashboard",
    labelKey: "sidebar.pages.dashboard",
    routePaths: ["/dashboard"],
    defaultPath: "/dashboard",
    access: { type: "authenticated", deniedRoles: ["evaluator"] },
    sidebar: { placement: "control", order: 10, iconName: "HomeOutlined", matchPaths: ["/dashboard"] },
    routing: {
      mode: "flat",
      routeId: "dashboard.main",
      mobileEnabled: true,
      postLoginChunkPreloadOrder: 10,
      postLoginChunkPreloadAudience: "standard",
    },
    i18n: { namespaces: ["filter"], includeEmbeddedWidgets: true },
    assignable: false,
    loadPage: () => import("../pages/dashboard"),
  },
  {
    key: "Obligations",
    label: "Obligations",
    labelKey: "sidebar.pages.obligations",
    routePaths: [
      "/obligations",
      "/obligations/:obligationId",
      "/obligations/:obligationId/:recurrenceId",
    ],
    defaultPath: "/obligations",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 10,
      iconName: "HiOutlineClipboardDocumentCheck",
      className: "app-sidebar-nav-cluster-item--obligations",
      matchPaths: [
        "/obligations",
        "/obligations/:obligationId",
        "/obligations/:obligationId/:recurrenceId",
      ],
    },
    routing: {
      mode: "flat",
      routeId: "obligations.main",
      mobileEnabled: true,
      mobileRouteId: "obligations.mobile",
      postLoginChunkPreloadOrder: 20,
      postLoginChunkPreloadAudience: "standard",
    },
    i18n: { namespaces: ["obligationsV2", "filter"], includeEmbeddedWidgets: true },
    assignable: true,
    assignmentOption: { color: "#0095A2", textColor: "#fff" },
    loadPage: () => import("../pages/obligations/index.jsx"),
    loadMobilePage: () => import("../pages/obligations/MobileObligationsView.jsx"),
  },
  {
    key: "Frameworks",
    label: "Frameworks",
    labelKey: "sidebar.pages.frameworks",
    routePaths: ["/frameworks", "/frameworks/:frameworkId"],
    defaultPath: "/frameworks",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 20,
      iconName: "MdOutlineAccountTree",
      className: "app-sidebar-nav-cluster-item--frameworks",
      matchPaths: ["/frameworks", "/frameworks/:frameworkId"],
    },
    routing: {
      mode: "flat",
      routeId: "frameworks.main",
      postLoginChunkPreloadOrder: 30,
      postLoginChunkPreloadAudience: "standard",
    },
    i18n: { namespaces: ["newFramework", "fromTemplate"], includeEmbeddedWidgets: true },
    assignable: true,
    assignmentOption: { color: "rgba(26,35,113,0.83)", textColor: "#fff" },
    loadPage: () => import("../pages/frameworks/index.jsx"),
  },
  {
    key: "Entities",
    label: "Entities",
    labelKey: "sidebar.pages.entities",
    routePaths: ["/entities", "/entities/dashboards"],
    defaultPath: "/entities",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 30,
      iconName: "MdOutlineBusiness",
      className: "app-sidebar-nav-cluster-item--entities",
      matchPaths: ["/entities", "/entities/dashboards"],
    },
    routing: {
      mode: "shell",
      routeId: "entities.root",
      mountPath: "/entities/*",
      postLoginChunkPreloadOrder: 40,
      postLoginChunkPreloadAudience: "standard",
    },
    i18n: {
      namespaces: ["entities", "fileTree"],
      includeEmbeddedWidgets: false,
      routeOverrides: [{ path: "/entities/dashboards", namespaces: ["entities"] }],
    },
    assignable: true,
    assignmentOption: { color: "#bcbcbc", textColor: "#f8fafc" },
    loadPage: () => import("../pages/entities/EntitiesModuleRoot.jsx"),
  },
  {
    key: "Contracts",
    label: "Contracts",
    labelKey: "sidebar.pages.contracts",
    routePaths: ["/contracts", "/contracts/:contractId"],
    defaultPath: "/contracts",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 40,
      iconName: "MdOutlineDescription",
      className: "app-sidebar-nav-cluster-item--contracts",
      matchPaths: ["/contracts", "/contracts/:contractId"],
    },
    routing: { mode: "flat", routeId: "contracts.main" },
    i18n: { namespaces: ["validations"], includeEmbeddedWidgets: true },
    assignable: true,
    assignmentOption: { color: "#797979", textColor: "#fff" },
    loadPage: () => import("../pages/contracts"),
  },
  {
    key: "Files",
    label: "Files",
    labelKey: "sidebar.pages.files",
    routePaths: ["/files"],
    defaultPath: "/files",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 50,
      iconName: "HiOutlineFolder",
      className: "app-sidebar-nav-cluster-item--files",
      matchPaths: ["/files"],
    },
    routing: {
      mode: "flat",
      routeId: "files.main",
      postLoginChunkPreloadOrder: 50,
      postLoginChunkPreloadAudience: "standard",
    },
    i18n: { namespaces: ["files", "fileTree", "filter", "dashboard"], includeEmbeddedWidgets: true },
    assignable: true,
    assignmentOption: { color: "#f8a112", textColor: "#fff" },
    loadPage: () => import("../pages/files/index.jsx"),
  },
  {
    key: "Evaluations",
    label: "Evaluations",
    labelKey: "sidebar.pages.evaluations",
    routePaths: ["/evaluations"],
    defaultPath: "/evaluations",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 60,
      iconName: "MdOutlineFactCheck",
      className: "app-sidebar-nav-cluster-item--evaluations",
      matchPaths: ["/evaluations"],
    },
    routing: {
      mode: "flat",
      routeId: "evaluations.main",
      postLoginChunkPreloadOrder: 10,
      postLoginChunkPreloadAudience: "evaluator",
    },
    i18n: { namespaces: ["evaluations", "validations"], includeEmbeddedWidgets: false },
    assignable: true,
    assignmentOption: { color: "#E64A19", textColor: "#fff" },
    loadPage: () => import("../pages/evaluations"),
  },
  {
    key: "Actions",
    label: "Actions",
    labelKey: "sidebar.pages.actions",
    routePaths: ["/actions", "/actions/:actionId"],
    defaultPath: "/actions",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 70,
      iconName: "FiCode",
      className: "app-sidebar-nav-cluster-item--actions",
      matchPaths: ["/actions", "/actions/:actionId"],
    },
    routing: { mode: "shell", routeId: "actions.root", mountPath: "/actions/*" },
    i18n: { namespaces: ["actions"], includeEmbeddedWidgets: false },
    assignable: true,
    assignmentOption: { color: "#30a4d7", textColor: "#fff" },
    loadPage: () => import("../pages/actions/ActionsModuleRoot.jsx"),
  },
  {
    key: "Formats",
    label: "Formats",
    labelKey: "sidebar.pages.formats",
    routePaths: ["/formats"],
    defaultPath: "/formats",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 80,
      iconName: "MdOutlineDescription",
      className: "app-sidebar-nav-cluster-item--formats",
      matchPaths: ["/formats"],
    },
    routing: { mode: "flat", routeId: "formats.main" },
    i18n: { namespaces: ["formats"], includeEmbeddedWidgets: false },
    assignable: true,
    assignmentOption: { color: "#d9d9d9", textColor: "#111827" },
    devOnly: true,
    loadPage: () => import("../pages/formats"),
  },
  {
    key: "Analyzer",
    label: "Analyzer",
    labelKey: "sidebar.pages.analyzer",
    routePaths: ["/analyzer"],
    defaultPath: "/analyzer",
    access: { type: "module" },
    sidebar: {
      placement: "nav",
      order: 90,
      iconName: "MdOutlineDescription",
      className: "app-sidebar-nav-cluster-item--analyzer",
      matchPaths: ["/analyzer"],
    },
    routing: { mode: "flat", routeId: "analyzer.main" },
    i18n: { namespaces: ["analyzer", "entities", "fileTree"], includeEmbeddedWidgets: false },
    assignable: true,
    assignmentOption: { color: "#123a4d", textColor: "#fff" },
    devOnly: true,
    loadPage: () => import("../pages/analyzer"),
  },
  {
    key: "Calendar",
    label: "Calendar",
    labelKey: "sidebar.pages.calendar",
    routePaths: ["/calendar"],
    defaultPath: "/calendar",
    access: { type: "module" },
    sidebar: {
      placement: "control",
      order: 20,
      iconName: "CalendarOutlined",
      matchPaths: ["/calendar"],
    },
    routing: { mode: "flat", routeId: "calendar.main" },
    i18n: { namespaces: ["calendar", "filter"], includeEmbeddedWidgets: true },
    assignable: true,
    assignmentOption: { color: "#2F855A", textColor: "#fff" },
    loadPage: () => import("../pages/calendar"),
  },
  {
    key: "Configuration",
    label: "Configuration",
    labelKey: "sidebar.pages.configuration",
    routePaths: ["/configuration"],
    defaultPath: "/configuration",
    access: { type: "module", requiredRoles: ["owner", "admin"] },
    sidebar: {
      placement: "none",
      order: 100,
      iconName: "SettingOutlined",
      matchPaths: ["/configuration"],
    },
    routing: { mode: "flat", routeId: "configuration.main" },
    i18n: { namespaces: ["configuration", "copy", "fromTemplate"], includeEmbeddedWidgets: true },
    assignable: true,
    assignmentOption: { color: "#334155", textColor: "#fff" },
    loadPage: () => import("../pages/configuration"),
  },
];

/**
 * Strip lazy loader fields before exposing metadata to i18n / registry hydration.
 * Keeps `import()` factories out of the login cold-start dependency graph surface.
 *
 * @param {ModuleDefinition} definition
 * @returns {Omit<ModuleDefinition, "loadPage"|"loadMobilePage">}
 */
export function toRegistryEntry({ loadPage, loadMobilePage, ...entry }) {
  return entry;
}

/** Registry metadata without lazy chunk loader factories. */
export const registryEntries = moduleDefinitions.map(toRegistryEntry);
